// deno-lint-ignore-file no-unused-vars
import { h, Fragment } from '../jsx-runtime.ts';
import type { TaskWithTags } from '../types.ts';
import { TasksAPI } from '../api.ts';
import { showToast } from './tmk-toast.tsx';
import { renderTaskMeta, type TaskMetaCallbacks } from './tmk-task-meta.tsx';
import { UndoManager } from '../undo.ts';
import { appendLinkified, setLinkified } from '../linkify.ts';

/** Operacje strukturalne przycisków ⤒ ⤓ ⇥ ⇤ — spec/lista-zadan.md "Przyciski przesuwania". */
export type ReorderAction = 'move_top' | 'move_bottom' | 'indent' | 'outdent';

export const REORDER_BUTTONS: readonly [ReorderAction, string, string][] = [
  ['move_top', '⤒', 'Na początek poziomu'],
  ['move_bottom', '⤓', 'Na koniec poziomu'],
  ['indent', '⇥', 'Poziom niżej — zagnieźdź pod element powyżej (Tab)'],
  ['outdent', '⇤', 'Poziom wyżej — odgnieźdź (Shift+Tab)'],
];

export interface TaskItemCallbacks extends Omit<TaskMetaCallbacks, 'onEditDescription'> {
  /** Opis rozwinięty w miejscu (spec: Ikony 📝 📎 🔁 → Opis w wierszu) — stan trzymany w liście, przeżywa rerender. */
  isDescExpanded: (taskId: string) => boolean;
  onDescToggled: (taskId: string, expanded: boolean) => void;
  onReorder: (task: TaskWithTags, action: ReorderAction) => void;
  onToggleCollapse: (taskId: string) => void;
  onToggleCompleted: (task: TaskWithTags) => void;
  onToggleCancelled: (task: TaskWithTags) => void;
  /** Panel szczegółów (opis, załączniki) renderowany WEWNĄTRZ karty, pod meta row; null = zwinięty. */
  renderExtras: (task: TaskWithTags) => HTMLElement | null;
  onDelete: (btn: HTMLElement, task: TaskWithTags) => void;
  onSetupDrag: (grip: HTMLElement, taskId: string, item: HTMLElement) => void;
  onSetupTouchDrag: (item: HTMLElement, taskId: string) => void;
  onSetupItemDragover: (item: HTMLElement, taskId: string) => void;
  onSetupSelection: (item: HTMLElement, taskId: string) => void;
  isSelected: (taskId: string) => boolean;
}

export function renderTaskItem(
  task: TaskWithTags,
  depth: number,
  hasChildren: boolean,
  isCollapsed: boolean,
  callbacks: TaskItemCallbacks,
): HTMLElement {
  const classes = ['task-item'];
  if (task.completed) classes.push('completed');
  if (task.cancelled) classes.push('cancelled');
  if (task._tagAncestor) classes.push('tag-ancestor');
  if (callbacks.isSelected(task.id)) classes.push('selected');

  const item = <div className={classes.join(' ')} data-task-id={task.id}
    style={{ marginLeft: `calc(${depth} * var(--indent-size, 12px))` }} tabindex="0"
  /> as HTMLElement;

  // Selection (long-press)
  callbacks.onSetupSelection(item, task.id);

  // Grip
  const grip = <span className="task-grip" draggable="true">⁝⁝</span> as HTMLElement;
  callbacks.onSetupDrag(grip, task.id, item);
  item.appendChild(grip);

  // Touch drag
  callbacks.onSetupTouchDrag(item, task.id);

  // Arrow
  const arrowClasses = 'task-arrow' + (hasChildren && !isCollapsed ? ' expanded' : '');
  const arrow = <span className={arrowClasses}>▶</span> as HTMLElement;
  if (hasChildren) {
    arrow.addEventListener('click', () => callbacks.onToggleCollapse(task.id));
  } else {
    arrow.style.visibility = 'hidden';
  }
  item.appendChild(arrow);

  // Checkbox
  const checkbox = <input type="checkbox" className="task-checkbox"
    checked={task.completed ? 'checked' : undefined}
    onChange={() => callbacks.onToggleCompleted(task)}
  /> as HTMLInputElement;
  item.appendChild(checkbox);

  // Body
  const body = renderTaskBody(task, callbacks);
  item.appendChild(body);

  // Przyciski przesuwania (hover; na dotyku ukryte przez CSS)
  const moveActions = <div className="task-move-actions" /> as HTMLElement;
  for (const [action, icon, title] of REORDER_BUTTONS) {
    moveActions.appendChild(<button type="button" className="task-move-btn" title={title}
      onClick={(e: Event) => { e.stopPropagation(); callbacks.onReorder(task, action); }}
    >{icon}</button> as HTMLElement);
  }
  // ⊘ Anuluj / ↺ Przywróć (spec/lista-zadan.md → Anulowanie zadania)
  moveActions.appendChild(<button type="button" className="task-move-btn task-cancel-btn"
    title={task.cancelled ? 'Przywróć zadanie (cofnij anulowanie)' : 'Anuluj zadanie'}
    onClick={(e: Event) => { e.stopPropagation(); callbacks.onToggleCancelled(task); }}
  >{task.cancelled ? '↺' : '⊘'}</button> as HTMLElement);
  item.appendChild(moveActions);

  // Delete
  const delBtn = <button className="task-delete-btn" title="Usuń zadanie"
    onClick={(e: Event) => { e.stopPropagation(); callbacks.onDelete(delBtn as HTMLElement, task); }}
  >✕</button> as HTMLElement;
  item.appendChild(delBtn);

  // Dragover
  callbacks.onSetupItemDragover(item, task.id);

  return item;
}

function renderTaskBody(task: TaskWithTags, callbacks: TaskItemCallbacks): HTMLElement {
  const body = <div className="task-body" /> as HTMLElement;

  // Title + open link inline
  const title = <div className="task-title" /> as HTMLElement;
  const titleText = <span /> as HTMLElement;
  appendLinkified(titleText, task.title || '');
  titleText.addEventListener('click', () => editTitle(title, task));
  title.appendChild(titleText);

  const openLink = <a className="task-open-link" href={'/task/' + task.id}
    onClick={(e: Event) => e.stopPropagation()} title="Otwórz szczegóły"
  >↗</a> as HTMLElement;
  title.appendChild(openLink);
  body.appendChild(title);

  const { edit: editDesc } = mountDescription(task, title, callbacks);

  // Meta + author/created_at in the same row (author pushed to the right edge)
  const meta = renderTaskMeta(task, { ...callbacks, onEditDescription: editDesc });
  if (task.author_email || task.created_at) {
    const author = task.author_email ? task.author_email.split('@')[0] : '';
    const created = task.created_at ? new Date(task.created_at) : null;
    const info = [author, created ? created.toLocaleDateString('pl') : ''].filter(Boolean).join(' · ');
    const tooltip = [
      task.author_email ? 'Autor: ' + task.author_email : '',
      created ? 'Utworzono: ' + created.toLocaleString('pl') : '',
    ].filter(Boolean).join('\n');
    meta.appendChild(<span className="task-author" title={tooltip}>{info}</span> as HTMLElement);
  }
  body.appendChild(meta);

  // Panel szczegółów (opis, załączniki) — wewnątrz karty, żeby było widać, że to jedna rzecz.
  const extras = callbacks.renderExtras(task);
  if (extras) body.appendChild(extras);

  return body;
}

function editTitle(titleDiv: HTMLElement, task: TaskWithTags) {
  if (titleDiv.querySelector('input')) return;
  const openLink = titleDiv.querySelector('.task-open-link') as HTMLElement | null;
  const input = <input type="text" className="inline-edit-input" value={task.title || ''} /> as HTMLInputElement;
  titleDiv.textContent = '';
  titleDiv.appendChild(input);
  input.focus();
  input.select();

  const restore = () => {
    titleDiv.textContent = '';
    const span = <span /> as HTMLElement;
    appendLinkified(span, task.title || '');
    span.addEventListener('click', () => editTitle(titleDiv, task));
    titleDiv.appendChild(span);
    if (openLink) titleDiv.appendChild(openLink);
  };

  const save = async () => {
    const val = input.value.trim();
    if (!val) { restore(); return; }
    if (val !== task.title) {
      try {
        UndoManager.push({ type: 'update', taskId: task.id, oldValues: { title: task.title } });
        await TasksAPI.update(task.id, { title: val });
        task.title = val;
      } catch (err: unknown) {
        showToast('Błąd zapisu tytułu: ' + (err as Error).message, 'error');
      }
    }
    restore();
  };

  input.addEventListener('blur', save);
  input.addEventListener('keydown', (e: Event) => {
    const ke = e as KeyboardEvent;
    if (ke.key === 'Enter') { e.preventDefault(); input.blur(); }
    if (ke.key === 'Escape') { restore(); }
  });
}

/** Callbacki potrzebne do opisu w wierszu (podzbiór TaskItemCallbacks — używane też w widoku płaskim). */
export type DescriptionCallbacks = Pick<TaskItemCallbacks, 'isDescExpanded' | 'onDescToggled' | 'onRerender'>;

/**
 * Opis w wierszu — montuje element `.task-desc` bezpośrednio pod `title` (spec: Ikony 📝 📎 🔁 → Opis w wierszu).
 * Zwraca `edit()` dla ikony 📝 (edycja w tym samym miejscu).
 */
export function mountDescription(task: TaskWithTags, title: HTMLElement, callbacks: DescriptionCallbacks): { edit: () => void } {
  // Opis w wierszu (spec: Ikony 📝 📎 🔁 → Opis w wierszu): wyświetlany RAZ — skrót (1 linia,
  // klikalny na szerokości tekstu) ⇄ pełny tekst w tym samym miejscu; 📝 w meta row = edycja w miejscu.
  let desc: HTMLElement | null = null;
  let expanded = callbacks.isDescExpanded(task.id);
  const ensureDesc = (): HTMLElement => {
    if (desc) return desc;
    desc = <div className="task-desc" /> as HTMLElement;
    // Podczas edycji zdarzenia nie wyciekają do wiersza (long-press zaznaczania, D&D).
    for (const type of ['mousedown', 'mouseup', 'dblclick']) {
      desc.addEventListener(type, (e: Event) => { if (desc?.querySelector('textarea')) e.stopPropagation(); });
    }
    desc.addEventListener('touchstart', (e: Event) => { if (desc?.querySelector('textarea')) e.stopPropagation(); }, { passive: true });
    // Klik 1 (skrót) = rozwiń; klik 2 (pełny tekst) = edytuj; „▴ zwiń” = zwiń (spec: Opis w wierszu).
    desc.addEventListener('click', (e: Event) => {
      e.stopPropagation();
      const target = e.target as HTMLElement;
      if (target.closest('a, textarea')) return;
      if (target.closest('.task-desc-collapse')) { setExpanded(false); return; }
      if (expanded) editDesc(); else setExpanded(true);
    });
    title.after(desc);
    return desc;
  };
  const renderDesc = () => {
    if (!task.description) { desc?.remove(); desc = null; return; }
    const el = ensureDesc();
    el.className = 'task-desc ' + (expanded ? 'task-desc-expanded' : 'task-desc-truncated');
    el.title = expanded ? 'Kliknij, aby edytować opis' : 'Kliknij, aby rozwinąć pełny opis';
    setLinkified(el, task.description);
    if (expanded) {
      el.appendChild(<span className="task-desc-collapse" title="Zwiń opis do jednej linii">▴ zwiń</span> as HTMLElement);
    }
  };
  const setExpanded = (v: boolean) => { expanded = v; callbacks.onDescToggled(task.id, v); renderDesc(); };
  const editDesc = () => {
    const el = ensureDesc();
    if (el.querySelector('textarea')) return;
    el.className = 'task-desc task-desc-editing';
    el.title = '';
    const textarea = <textarea className="detail-desc-textarea" rows="4" placeholder="Opis zadania…" /> as HTMLTextAreaElement;
    textarea.value = task.description || '';
    el.textContent = '';
    el.appendChild(textarea);
    textarea.focus();
    const autoResize = () => { textarea.style.height = 'auto'; textarea.style.height = Math.max(96, textarea.scrollHeight) + 'px'; };
    autoResize();
    textarea.addEventListener('input', autoResize);
    let cancelled = false;
    textarea.addEventListener('keydown', (e: Event) => {
      // Escape = anuluj bez zapisu (spec: Inline editing); nie może zwinąć niczego wyżej.
      if ((e as KeyboardEvent).key === 'Escape') { e.stopPropagation(); cancelled = true; textarea.blur(); }
    });
    textarea.addEventListener('blur', async () => {
      const val = cancelled ? (task.description || '') : textarea.value.trim();
      if (val !== (task.description || '')) {
        try {
          UndoManager.push({ type: 'update', taskId: task.id, oldValues: { description: task.description } });
          await TasksAPI.update(task.id, { description: val || null });
          task.description = val || null;
          if (val) { expanded = true; callbacks.onDescToggled(task.id, true); }
          await callbacks.onRerender();   // odświeża 📝 (wyszarzenie) i opis w wierszu
          return;
        } catch (err: unknown) {
          showToast('Błąd zapisu opisu: ' + (err as Error).message, 'error');
        }
      }
      renderDesc();
    });
  };
  renderDesc();
  return { edit: editDesc };
}
